// Vessel readiness score (Dashboard): 0-100, start at 100 and subtract weighted penalties. Plain JS: tests/dashboard-v2.test.mjs.
//
//   area                        penalty                                   cap
//   Maintenance overdue         share of tasks overdue x 100              -30   (30 % of the tasks overdue = the cap)
//   Ship documents expired      -2 each                                   -10
//   Crew certificates expired   -5 each                                   -15
//   Crew certificates < 30 days -1 each                                   -5
//   Drills overdue              -3 each                                   (no cap)
//   Drills due                  -1 each                                   (no cap)
//   Equipment out of service    -2 each                                   (no cap)
//   Stock out of stock          -1 each                                   -5
//   ISM major non-conformity    -10 each, open                            (no cap)
//
// The score is rounded and kept between 0 and 100. A module the login cannot see is passed as 0 (it costs nothing).
// Trend: the daily snapshots (readiness_snapshots, sql/fleet-4.sql; src/lib/readinessTrend.js) - "—" until there is one from 7 days ago.
import { expiryStatus } from './expiry.js'
export const READINESS_RULES = [
  { key: 'maintenance', label: 'Overdue maintenance', each: null, max: 30 },
  { key: 'docs', label: 'Expired ship documents', each: 2, max: 10 },
  { key: 'crewExpired', label: 'Expired crew certificates', each: 5, max: 15 },
  { key: 'crewSoon', label: 'Crew certificates expiring < 30 days', each: 1, max: 5 },
  { key: 'drillsOverdue', label: 'Overdue drills', each: 3, max: Infinity },
  { key: 'drillsDue', label: 'Drills due', each: 1, max: Infinity },
  { key: 'equipment', label: 'Equipment out of service', each: 2, max: Infinity },
  { key: 'stock', label: 'Out of stock', each: 1, max: 5 },
  { key: 'ismMajor', label: 'Open ISM major non-conformities', each: 10, max: Infinity },
]
const n = v => (Number.isFinite(+v) && +v > 0 ? +v : 0)

// in: { tasksTotal, tasksOverdue, docsExpired, crewExpired, crewSoon, drillsOverdue, drillsDue, equipmentOos, outOfStock, ismMajor }
export function readiness(inp = {}) {
  const c = {
    maintenance: n(inp.tasksTotal) ? Math.min(n(inp.tasksOverdue), n(inp.tasksTotal)) / n(inp.tasksTotal) * 100 : 0,
    docs: n(inp.docsExpired), crewExpired: n(inp.crewExpired), crewSoon: n(inp.crewSoon),
    drillsOverdue: n(inp.drillsOverdue), drillsDue: n(inp.drillsDue),
    equipment: n(inp.equipmentOos), stock: n(inp.outOfStock), ismMajor: n(inp.ismMajor),
  }
  const parts = READINESS_RULES.map(r => {
    const raw = r.each == null ? c[r.key] : c[r.key] * r.each
    const lost = Math.round(Math.min(r.max, raw) * 10) / 10
    return { key: r.key, label: r.label, count: r.key === 'maintenance' ? n(inp.tasksOverdue) : c[r.key], points: lost, capped: raw > r.max }
  })
  const total = parts.reduce((s, p) => s + p.points, 0)
  const score = Math.max(0, Math.min(100, Math.round(100 - total)))
  return { score, lost: Math.round(total * 10) / 10, parts: parts.filter(p => p.points > 0).sort((a, b) => b.points - a.points) }
}
// the inputs from the rows (fetchRows display names), the same for the vessel Dashboard and the Fleet page (fleet-1):
//   d = { tasks, docs, crewDocs, eq, inv, drills (drillsNeedingAttention), ismMajor }; see(module) -> may this login see it (0 if not)
//   today 'YYYY-MM-DD' (crew certificate days)
const cell = v => (v == null ? '' : typeof v === 'object' && !Array.isArray(v) ? String(v.value || v.name || v.label || '') : Array.isArray(v) ? (v.length ? cell(v[0]) : '') : String(v))
export function readinessInputs(d = {}, see = () => true, today) {
  const sv = m => (see(m) ? 1 : 0)
  const st = r => cell(r && r['Status'])
  const tasks = d.tasks || [], docs = d.docs || [], eq = d.eq || [], inv = d.inv || [], drills = d.drills || []
  // ship documents / crew certificates: the shared expiry rule (src/lib/expiry.js) on the date, never the stored Status
  const crewSt = (d.crewDocs || []).map(r => expiryStatus(r['Expiry Date'], today))
  return {
    tasksTotal: sv('maintenance') * tasks.length, tasksOverdue: sv('maintenance') * tasks.filter(t => st(t) === 'Overdue').length,
    docsExpired: sv('documents') * docs.filter(x => expiryStatus(x['Expiry Date'], today).expired).length,
    crewExpired: sv('crew') * crewSt.filter(s => s.expired).length, crewSoon: sv('crew') * crewSt.filter(s => s.days != null && s.days >= 0 && s.days <= 30).length,
    drillsOverdue: sv('checklists') * drills.filter(a => a.status === 'overdue').length, drillsDue: sv('checklists') * drills.filter(a => a.status === 'due').length,
    equipmentOos: sv('equipment') * eq.filter(e => st(e) === 'Out of Service').length, outOfStock: sv('inventory') * inv.filter(i => st(i) === 'Out of Stock').length,
    ismMajor: sv('ism_audits') * n(d.ismMajor),
  }
}
// trend against the score of 7 days ago (a stored snapshot); null = no snapshot -> "—"
export const trendVs7 = (score, old) => (old == null || !Number.isFinite(+old) ? null : score - Math.round(+old))
export const ringTone = s => (s >= 85 ? 'good' : s >= 65 ? 'mid' : 'low')   // green / amber / red
